import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { PiWarningCircleLight, PiArrowLeftLight, PiArrowRightLight, PiBriefcaseLight, PiCheckCircleLight, PiCaretRightLight, PiFileTextLight, PiGraduationCapLight, PiCircleNotchLight, PiMapPinLight, PiPauseLight, PiPlayLight, PiPlusLight, PiFloppyDiskLight, PiMagnifyingGlassLight, PiTargetLight, PiUploadSimpleLight, PiUsersLight, PiXLight } from 'react-icons/pi';
import useJobStore from '../../store/jobStore';
import useJobProcessingStore from '../../store/jobProcessingStore';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import JobProfileQuestionStepper, {
  jobQuestionInitialValue,
  toJobProfileAnswerValue
} from '../../components/jobs/JobProfileQuestionStepper';
import { SALARY_PERIODS, USD_CURRENCY, salaryPeriodLabel } from '../../utils/compensation';

const jobTypes = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'internship', label: 'Internship' }
];

const experienceLevels = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'executive', label: 'Executive' }
];

const steps = [
  { id: 1, label: 'Source', icon: PiUploadSimpleLight },
  { id: 2, label: 'Role', icon: PiBriefcaseLight },
  { id: 3, label: 'Requirements', icon: PiFileTextLight },
  { id: 4, label: 'Matching', icon: PiTargetLight },
  { id: 5, label: 'Review', icon: PiCheckCircleLight },
  { id: 6, label: 'Match profile', icon: PiUsersLight }
];

const AI_FIELD_SECTIONS = {
  title: 'role',
  jobType: 'role',
  experienceLevel: 'role',
  location: 'role',
  isRemoteAllowed: 'role',
  description: 'role',
  requirementsText: 'requirements',
  nonNegotiableFilters: 'requirements',
  payRangeMin: 'compensation',
  payRangeMax: 'compensation',
  payCurrency: 'compensation',
  payPeriod: 'compensation',
  contractDurationMonths: 'compensation',
  targetStartDate: 'compensation'
};

const EXTRACTION_STEP_SECTIONS = {
  2: 'role',
  3: 'requirements',
  4: 'compensation'
};

const COMPLETE_CORE_STAGES = new Set([
  'core_sections_ready',
  'applying_sections',
  'building_matching_profile',
  'ready_for_review',
  'completed'
]);

const TERMINAL_EXTRACTION_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const BACKGROUND_EXTRACTION_STAGES = new Set([
  'core_sections_ready',
  'applying_sections',
  'building_matching_profile',
  'matching_profile_queued'
]);
const PUBLISH_READY_EXTRACTION_STAGES = new Set(['core_sections_ready', 'applying_sections']);

const isBackgroundExtractionRun = (run) => (
  ['pending', 'processing'].includes(run?.status) && BACKGROUND_EXTRACTION_STAGES.has(run?.stage)
);

const isBlockingExtractionRun = (run) => (
  ['pending', 'processing'].includes(run?.status) && !isBackgroundExtractionRun(run)
);

const initialFormData = {
  title: '',
  description: '',
  requirementsText: '',
  jobType: 'full_time',
  experienceLevel: 'mid',
  location: '',
  isRemoteAllowed: false,
  payRangeMin: '',
  payRangeMax: '',
  payCurrency: 'USD',
  payPeriod: '',
  contractDurationMonths: '',
  targetStartDate: '',
  matchBandPreference: 'A',
  positionsAvailable: 1,
  nonNegotiableFilters: [],
  publishAfterCreate: true
};

const labelClass = 'mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500';

const labelize = (value) => (
  String(value || 'Not set').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
);

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const scoreValue = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, numeric <= 1 ? numeric * 100 : numeric));
};

const percentText = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${Math.round(scoreValue(numeric))}%`;
};

const compactMoney = (value, currency = '') => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${currency ? `${currency} ` : ''}${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const buildCompensationConversionNotice = (run) => {
  const compensation = {
    ...(run?.coreData || {}),
    ...(run?.sections?.compensation || {})
  };
  const status = compensation.payConversionStatus;
  if (!status || status === 'not_applicable' || status === 'native_usd') return null;

  const sourceCurrency = compensation.rawPayCurrency;
  const sourceMin = compactMoney(compensation.rawPayRangeMin, sourceCurrency);
  const sourceMax = compactMoney(compensation.rawPayRangeMax);
  const period = salaryPeriodLabel(compensation.rawPayPeriod) || compensation.rawPayPeriod;
  const sourceRange = sourceMin && sourceMax
    ? `${sourceMin} - ${sourceMax}${period ? ` / ${period.toLowerCase()}` : ''}`
    : compensation.rawPayText || 'the source compensation';

  if (status === 'converted') {
    const provider = compensation.payFxProvider || 'FX provider';
    const rateDate = compensation.payFxRateDate ? `, ${compensation.payFxRateDate}` : '';
    return {
      tone: 'info',
      message: `Converted ${sourceRange} to USD for matching using ${provider}${rateDate}. Review the USD range before publishing.`
    };
  }

  const readableStatus = labelize(status);
  return {
    tone: 'warning',
    message: `The source compensation (${sourceRange}) needs review before it can be used for USD matching: ${readableStatus}.`
  };
};

const evidenceSummaryFor = (match = {}) => {
  const candidate = match.candidate || {};
  const evidence = match.insights?.candidateEvidence || {};
  const snapshot = match.insights?.candidateSnapshot || {};
  const coverage = evidence.evidenceCoverage || candidate.evidenceCoverage || {};
  const education = asArray(evidence.education || candidate.education);
  const experience = asArray(evidence.experience || candidate.experience);
  const institution = education.find((item) => item?.institution || item?.institution_name);
  const roleRows = [...experience, evidence.currentRole || candidate.currentRole].filter(Boolean);
  const role = roleRows.find((item) => item?.companyContextCheck || item?.company_context_check) ||
    roleRows.find((item) => item?.company_name || item?.companyName);
  const coverageScore = coverage.overallScore ?? coverage.overall_score;
  const evidenceCount = [
    evidence.skills || candidate.skills,
    evidence.experience || candidate.experience,
    evidence.education || candidate.education,
    evidence.certifications || candidate.certifications,
    evidence.projects || candidate.projects,
    match.insights?.evidenceSignals || match.microPoints
  ].reduce((total, value) => total + asArray(value).length, 0);

  return {
    institution,
    role,
    chips: [
      coverageScore !== null && coverageScore !== undefined ? `Coverage ${percentText(coverageScore)}` : null,
      snapshot.cvTextExtractionConfidence !== null && snapshot.cvTextExtractionConfidence !== undefined ? `CV extracted ${percentText(snapshot.cvTextExtractionConfidence)}` : null,
      evidenceCount ? `${evidenceCount} evidence signals` : null
    ].filter(Boolean).slice(0, 3)
  };
};

const jobValidationFieldAliases = {
  requirements_text: 'requirementsText',
  job_type: 'jobType',
  experience_level: 'experienceLevel',
  is_remote_allowed: 'isRemoteAllowed',
  pay_range_min: 'payRangeMin',
  pay_range_max: 'payRangeMax',
  pay_currency: 'payCurrency',
  pay_period: 'payPeriod',
  contract_duration_months: 'contractDurationMonths',
  target_start_date: 'targetStartDate',
  match_band_preference: 'matchBandPreference',
  positions_available: 'positionsAvailable',
  non_negotiable_filters: 'nonNegotiableFilters',
  posting_method: 'postingMethod',
  auto_publish: 'publishAfterCreate',
  autoPublish: 'publishAfterCreate'
};

const jobValidationFieldLabels = {
  postingMethod: 'Job source',
  title: 'Job title',
  jobType: 'Job type',
  experienceLevel: 'Experience level',
  location: 'Location',
  isRemoteAllowed: 'Remote work',
  description: 'Description',
  requirementsText: 'Requirements text',
  nonNegotiableFilters: 'Non-negotiable filters',
  payRangeMin: 'Minimum compensation',
  payRangeMax: 'Maximum compensation',
  payCurrency: 'Currency',
  payPeriod: 'Pay period',
  positionsAvailable: 'Positions available',
  contractDurationMonths: 'Contract months',
  targetStartDate: 'Target start date',
  matchBandPreference: 'Preferred match band',
  publishAfterCreate: 'Post-create action'
};

const jobValidationStepFields = {
  1: ['postingMethod'],
  2: ['title', 'jobType', 'experienceLevel', 'location', 'isRemoteAllowed', 'description'],
  3: ['requirementsText', 'nonNegotiableFilters'],
  4: ['payRangeMin', 'payRangeMax', 'payCurrency', 'payPeriod', 'positionsAvailable', 'contractDurationMonths', 'targetStartDate', 'matchBandPreference', 'publishAfterCreate']
};

const jobValidationFieldSteps = Object.entries(jobValidationStepFields).reduce((result, [step, fields]) => {
  fields.forEach(field => {
    result[field] = Number(step);
  });
  return result;
}, {});

const normalizeJobValidationField = (field) => {
  const normalized = String(field || '').replace(/\[(\d+)\]/g, '.$1');
  if (!normalized) return 'submit';

  const root = normalized.split('.')[0];
  return jobValidationFieldAliases[normalized] || jobValidationFieldAliases[root] || root || 'submit';
};

const getJobValidationMessage = (detail = {}) => detail.message || detail.msg || detail.error || '';

const mergeValidationError = (target, field, message) => {
  if (!message) return;
  target[field] = target[field] ? `${target[field]} ${message}` : message;
};

const buildJobValidationErrors = (details = []) => {
  const nextErrors = {};

  details.forEach(detail => {
    const field = normalizeJobValidationField(detail.field || detail.param || detail.name || (Array.isArray(detail.path) ? detail.path.join('.') : detail.path));
    const message = getJobValidationMessage(detail);
    mergeValidationError(nextErrors, field, message);
  });

  return nextErrors;
};

const getFirstJobValidationStep = (validationErrors = {}) => (
  Object.keys(validationErrors)
    .map(field => jobValidationFieldSteps[field])
    .filter(Boolean)
    .sort((a, b) => a - b)[0] || null
);

const getFirstJobValidationMessage = (validationErrors = {}, fallback = 'Validation failed') => (
  Object.entries(validationErrors)
    .find(([, message]) => Boolean(message))?.[1] || fallback
);

const getJobValidationFieldLabel = (field) => jobValidationFieldLabels[field] || labelize(field);

const FieldError = ({ message }) => {
  if (!message) return null;

  return (
    <p className="mt-1 flex items-start gap-1 text-xs text-rose-600">
      <PiWarningCircleLight size={12} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </p>
  );
};

const logJobDocumentUploadStep = (step, payload = {}) => {
  try {
    console.info('JOB_DOCUMENT_UPLOAD_JSON', JSON.stringify({
      event: 'job_document_upload_step',
      step,
      timestamp: new Date().toISOString(),
      ...payload
    }, null, 2));
  } catch (_error) {
    console.info('JOB_DOCUMENT_UPLOAD_JSON', step, payload);
  }
};

const fileMetadata = (file) => ({
  name: file?.name || null,
  type: file?.type || null,
  size: file?.size || 0,
  lastModified: file?.lastModified || null
});

const MatchCard = ({ match, onAction, actionLoading }) => {
  const candidate = match.candidate || {};
  const evidenceSummary = evidenceSummaryFor(match);
  const initials = (candidate.fullName || `${candidate.firstName || ''} ${candidate.lastName || ''}`)
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'CA';

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sm font-medium text-sky-700">
              {initials}
            </div>
            <div className="min-w-0">
              <h4 className="truncate text-sm font-medium text-[#0b1f3a]">{candidate.fullName || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate'}</h4>
              <p className="mt-1 text-xs text-slate-500">
                {labelize(candidate.experienceLevel)} {candidate.experienceYears ? ` · ${candidate.experienceYears} yrs` : ''} {candidate.location ? ` · ${candidate.location}` : ''}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              Band {match.matchBand || match.band || '-'} · {Math.round(Number(match.overallMatchScore || match.score || 0))}%
            </span>
            <span className="rounded-md bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 capitalize">
              {labelize(match.matchStatus || 'potential')}
            </span>
          </div>

          {match.explanation?.summary && (
            <p className="mt-3 text-sm leading-6 text-slate-600">{match.explanation.summary}</p>
          )}

          {candidate.skills?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {candidate.skills.slice(0, 6).map((skill, index) => (
                <span key={`${skill.skill_name || skill.skillName || index}`} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
                  {skill.skill_name || skill.skillName || skill.name || skill}
                </span>
              ))}
            </div>
          )}

          {(evidenceSummary.role || evidenceSummary.institution || evidenceSummary.chips.length > 0) && (
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {evidenceSummary.role && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  <div className="flex items-center gap-1.5 font-medium text-[#0b1f3a]"><PiBriefcaseLight size={12} /> {evidenceSummary.role.company_name || evidenceSummary.role.companyName || 'Company context'}</div>
                  <div>{evidenceSummary.role.job_title || evidenceSummary.role.jobTitle || 'Role evidence'}</div>
                </div>
              )}
              {evidenceSummary.institution && (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  <div className="flex items-center gap-1.5 font-medium text-[#0b1f3a]"><PiGraduationCapLight size={12} /> {evidenceSummary.institution.institution || evidenceSummary.institution.institution_name}</div>
                  <div>{[evidenceSummary.institution.degree || evidenceSummary.institution.degree_type, evidenceSummary.institution.field || evidenceSummary.institution.field_of_study].filter(Boolean).join(' · ') || 'Education evidence'}</div>
                </div>
              )}
              {evidenceSummary.chips.length > 0 && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-800 md:col-span-2">
                  <span className="font-medium text-[#0b1f3a]">Evidence readiness:</span> {evidenceSummary.chips.join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => onAction(match.matchId, 'interested')}
            disabled={actionLoading || match.employerAction === 'interested' || match.actionTaken === 'interested'}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0b1f3a] px-3 text-xs font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {actionLoading ? <PiCircleNotchLight className="animate-spin" size={12} /> : 'Express interest'}
          </button>
          <button
            onClick={() => onAction(match.matchId, 'not_interested')}
            disabled={actionLoading || match.employerAction === 'rejected'}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Pass
          </button>
        </div>
      </div>
    </article>
  );
};

const CreateJobPage = () => {
  const navigate = useNavigate();
  const {
    createJob,
    updateJob,
    fetchJobProfileSetup,
    saveJobProfileAnswers,
    publishJob,
    takeActionOnImmediateMatch,
    matchingConfiguration,
    fetchMatchingConfiguration,
    loading
  } = useJobStore();
  const startImport = useJobProcessingStore(state => state.startImport);
  const processingRuns = useJobProcessingStore(state => state.runs);

  const [currentStep, setCurrentStep] = useState(1);
  const [postingMethod, setPostingMethod] = useState('manual');
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [newFilter, setNewFilter] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseState, setParseState] = useState({ loading: false, error: '', metadata: null, warnings: [] });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [extractionRunId, setExtractionRunId] = useState(null);
  const [dirtyFields, setDirtyFields] = useState({});
  const [aiPopulatedFields, setAiPopulatedFields] = useState({});
  const [autoFollowExtraction, setAutoFollowExtraction] = useState(false);
  const stepContentRef = useRef(null);

  const getInputClass = (field) => {
    const base = 'w-full rounded-lg px-3 py-2.5 text-sm text-slate-700 outline-none transition-all duration-500 placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100';
    return `${base} ${aiPopulatedFields[field] ? 'animate-magic-highlight border-sky-400 bg-sky-50/30 shadow-[0_0_12px_rgba(56,189,248,0.2)]' : 'border border-slate-200 bg-white'}`;
  };

  const isFieldExtracting = (fieldName) => {
    if (!extractionRun?.stage || TERMINAL_EXTRACTION_STATUSES.has(extractionRun.status)) return false;
    if (dirtyFields[fieldName]) return false;

    const section = AI_FIELD_SECTIONS[fieldName];
    if (!section) return false;
    if (Object.prototype.hasOwnProperty.call(extractionRun.sections || {}, section)) return false;
    if (extractionRun.stage === `core_${section}_ready`) return false;
    if (COMPLETE_CORE_STAGES.has(extractionRun.stage)) return false;

    return true;
  };
  const [createdJob, setCreatedJob] = useState(null);
  const [profileSetup, setProfileSetup] = useState(null);
  const [answers, setAnswers] = useState({});
  const [skippedAnswers, setSkippedAnswers] = useState({});
  const [savingProfileQuestionKey, setSavingProfileQuestionKey] = useState('');
  const [dirtyProfileQuestionKeys, setDirtyProfileQuestionKeys] = useState({});
  const [recentlySavedProfileQuestionKeys, setRecentlySavedProfileQuestionKeys] = useState({});
  const [activeProfileQuestionIndex, setActiveProfileQuestionIndex] = useState(0);
  const [profileQuestionsFinished, setProfileQuestionsFinished] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchingResults, setMatchingResults] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [configurationApplied, setConfigurationApplied] = useState(false);

  const matchingPreferences = matchingConfiguration?.preferences || {};
  const extractionRun = extractionRunId ? processingRuns[String(extractionRunId)] : null;
  const compensationConversionNotice = buildCompensationConversionNotice(extractionRun);
  const extractionBackground = isBackgroundExtractionRun(extractionRun);
  const extractionActive = isBlockingExtractionRun(extractionRun);
  const extractionReadyForPublish = (
    ['pending', 'processing'].includes(extractionRun?.status) &&
    PUBLISH_READY_EXTRACTION_STAGES.has(extractionRun?.stage)
  );
  const jobPublished = createdJob?.status === 'active';
  const jobFlowStarted = Boolean(createdJob) || currentStep > 1;

  const isExtractionSectionReady = (section) => {
    if (!extractionRun || !section) return false;
    if (Object.prototype.hasOwnProperty.call(extractionRun.sections || {}, section)) return true;
    if (extractionRun.stage === `core_${section}_ready`) return true;
    if (COMPLETE_CORE_STAGES.has(extractionRun.stage)) return true;
    return TERMINAL_EXTRACTION_STATUSES.has(extractionRun.status);
  };

  const isStepAvailable = (stepId) => {
    if (stepId === 1) return !createdJob;
    if (stepId >= 2 && stepId <= 5) return jobFlowStarted;
    return stepId === 6 && (currentStep === 6 || Boolean(profileSetup));
  };

  const isStepComplete = (stepId) => {
    if (stepId === 1) return jobFlowStarted;
    const extractionSection = EXTRACTION_STEP_SECTIONS[stepId];
    if (extractionSection && extractionRun) return isExtractionSectionReady(extractionSection);
    return currentStep > stepId;
  };

  const selectStep = (stepId) => {
    if (!isStepAvailable(stepId)) return;
    setAutoFollowExtraction(false);
    setCurrentStep(stepId);
  };

  useEffect(() => {
    if (!autoFollowExtraction || !createdJob || !extractionRun) return;

    const sections = extractionRun.sections || {};
    let nextExtractionStep = 2;

    if (Object.prototype.hasOwnProperty.call(sections, 'requirements')) nextExtractionStep = 3;
    if (Object.prototype.hasOwnProperty.call(sections, 'compensation')) nextExtractionStep = 4;
    if (
      COMPLETE_CORE_STAGES.has(extractionRun.stage) ||
      TERMINAL_EXTRACTION_STATUSES.has(extractionRun.status)
    ) {
      nextExtractionStep = 5;
    }

    if (currentStep === nextExtractionStep) return;
    setCurrentStep(nextExtractionStep);
    window.requestAnimationFrame(() => {
      stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [autoFollowExtraction, createdJob, currentStep, extractionRun]);

  useEffect(() => {
    fetchMatchingConfiguration();
  }, [fetchMatchingConfiguration]);

  useEffect(() => {
    if (configurationApplied || !matchingConfiguration?.preferences) return;
    const preferredBands = matchingConfiguration.preferences.preferredBands || [];
    setFormData(prev => ({
      ...prev,
      publishAfterCreate: matchingConfiguration.preferences.enableImmediateMatching !== false,
      matchBandPreference: preferredBands[0] || prev.matchBandPreference
    }));
    setConfigurationApplied(true);
  }, [configurationApplied, matchingConfiguration]);

  const updateForm = (field, value) => {
    if (extractionRun) setAutoFollowExtraction(false);
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirtyFields(prev => ({ ...prev, [field]: true }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  useEffect(() => {
    if (!extractionRun) return;
    const extracted = {
      ...(extractionRun.coreData || {}),
      ...(extractionRun.sections?.role || {}),
      ...(extractionRun.sections?.requirements || {}),
      ...(extractionRun.sections?.compensation || {})
    };

    setFormData(prev => {
      const next = { ...prev };
      let changed = false;
      const newlyPopulated = {};
      Object.entries(extracted).forEach(([field, value]) => {
        if (dirtyFields[field] || value === null || value === undefined || value === '') return;
        if (field === 'jobType' && !jobTypes.some(type => type.value === value)) return;
        if (field === 'experienceLevel' && !experienceLevels.some(level => level.value === value)) return;
        if (field === 'payCurrency' && value !== USD_CURRENCY) return;
        if (field === 'nonNegotiableFilters' && !Array.isArray(value)) return;
        if (Object.prototype.hasOwnProperty.call(next, field) && next[field] !== value) {
          next[field] = value;
          newlyPopulated[field] = true;
          changed = true;
        }
      });
      if (changed) {
        setAiPopulatedFields(prevAi => ({ ...prevAi, ...newlyPopulated }));
      }
      return changed ? next : prev;
    });
  }, [dirtyFields, extractionRun]);

  useEffect(() => {
    const highlightedFields = Object.keys(aiPopulatedFields).filter(field => aiPopulatedFields[field]);
    if (!highlightedFields.length) return;

    const timer = setTimeout(() => {
      setAiPopulatedFields(prev => {
        const next = { ...prev };
        highlightedFields.forEach(field => {
          delete next[field];
        });
        return next;
      });
    }, 1800);

    return () => clearTimeout(timer);
  }, [aiPopulatedFields]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    updateForm(name, type === 'checkbox' ? checked : value);
  };

  const getStepValidationErrors = (step) => {
    const nextErrors = {};
    const hasValue = (value) => value !== '' && value !== null && value !== undefined;

    if (step === 2) {
      const title = formData.title.trim();
      const description = formData.description.trim();
      const location = formData.location.trim();

      if (title.length < 3) nextErrors.title = 'Job title must be at least 3 characters.';
      else if (title.length > 200) nextErrors.title = 'Job title must be 200 characters or less.';

      if (!jobTypes.some(type => type.value === formData.jobType)) nextErrors.jobType = 'Select a valid job type.';
      if (!experienceLevels.some(level => level.value === formData.experienceLevel)) nextErrors.experienceLevel = 'Select a valid experience level.';

      if (location.length < 2) nextErrors.location = 'Location is required.';
      else if (location.length > 200) nextErrors.location = 'Location must be 200 characters or less.';

      if (description.length < 50) nextErrors.description = 'Description must be at least 50 characters.';
      else if (description.length > 5000) nextErrors.description = 'Description must be 5000 characters or less.';
    }

    if (step === 3) {
      const requirementsText = formData.requirementsText.trim();
      if (requirementsText.length < 20) nextErrors.requirementsText = 'Requirements must be at least 20 characters.';
      else if (requirementsText.length > 2000) nextErrors.requirementsText = 'Requirements must be 2000 characters or less.';
    }

    if (step === 4) {
      const payMinProvided = hasValue(formData.payRangeMin);
      const payMaxProvided = hasValue(formData.payRangeMax);
      const min = Number(formData.payRangeMin);
      const max = Number(formData.payRangeMax);

      if (payMinProvided && (!Number.isFinite(min) || min <= 0)) {
        nextErrors.payRangeMin = 'Minimum compensation must be a positive number.';
      }
      if (payMaxProvided && (!Number.isFinite(max) || max <= 0)) {
        nextErrors.payRangeMax = 'Maximum compensation must be a positive number.';
      }
      if (payMinProvided && !payMaxProvided) {
        nextErrors.payRangeMax = 'Provide both minimum and maximum compensation, or leave both blank.';
      } else if (!payMinProvided && payMaxProvided) {
        nextErrors.payRangeMin = 'Provide both minimum and maximum compensation, or leave both blank.';
      } else if (payMinProvided && payMaxProvided && !nextErrors.payRangeMin && !nextErrors.payRangeMax && min >= max) {
        nextErrors.payRangeMax = 'Maximum compensation must be greater than minimum.';
      }

      if (formData.payCurrency !== USD_CURRENCY) nextErrors.payCurrency = 'Compensation currency must be USD.';
      if ((payMinProvided || payMaxProvided) && !formData.payPeriod) {
        nextErrors.payPeriod = 'Select the period for this compensation range.';
      }
      if (!payMinProvided && !payMaxProvided && formData.payPeriod) {
        nextErrors.payPeriod = 'Add a compensation range before selecting a period.';
      }

      const positions = Number(formData.positionsAvailable);
      if (!Number.isInteger(positions) || positions < 1) nextErrors.positionsAvailable = 'At least one whole position is required.';
      else if (positions > 1000) nextErrors.positionsAvailable = 'Positions available cannot exceed 1000.';

      if (hasValue(formData.contractDurationMonths)) {
        const contractMonths = Number(formData.contractDurationMonths);
        if (!Number.isInteger(contractMonths) || contractMonths < 1 || contractMonths > 60) {
          nextErrors.contractDurationMonths = 'Contract duration must be a whole number from 1 to 60 months.';
        }
      }

      if (formData.targetStartDate && Number.isNaN(Date.parse(formData.targetStartDate))) {
        nextErrors.targetStartDate = 'Enter a valid target start date.';
      }

      if (!['A', 'B', 'C'].includes(formData.matchBandPreference)) {
        nextErrors.matchBandPreference = 'Select a valid match band.';
      }
    }

    return nextErrors;
  };

  const replaceErrorsForFields = (fields, fieldErrors) => {
    setErrors(prev => {
      const next = { ...prev };
      fields.forEach(field => delete next[field]);
      return { ...next, ...fieldErrors };
    });
  };

  const validateStep = (step) => {
    const nextErrors = getStepValidationErrors(step);
    replaceErrorsForFields(jobValidationStepFields[step] || [], nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateCreateForm = () => {
    const createSteps = [2, 3, 4];
    const nextErrors = createSteps.reduce((result, step) => ({
      ...result,
      ...getStepValidationErrors(step)
    }), {});
    const fields = createSteps.flatMap(step => jobValidationStepFields[step] || []);

    replaceErrorsForFields([...fields, 'submit'], nextErrors);

    const firstStep = getFirstJobValidationStep(nextErrors);
    if (firstStep) setCurrentStep(firstStep);

    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = () => {
    setAutoFollowExtraction(false);
    if (extractionActive) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      return;
    }
    if (validateStep(currentStep)) setCurrentStep(prev => Math.min(prev + 1, 5));
  };

  const prevStep = () => {
    if (currentStep === 6) return;
    setAutoFollowExtraction(false);
    setCurrentStep(prev => Math.max(prev - 1, createdJob ? 2 : 1));
  };

  const addNonNegotiableFilter = () => {
    const value = newFilter.trim();
    if (!value || formData.nonNegotiableFilters.includes(value)) return;
    updateForm('nonNegotiableFilters', [...formData.nonNegotiableFilters, value]);
    setNewFilter('');
  };

  const removeNonNegotiableFilter = (filter) => {
    updateForm('nonNegotiableFilters', formData.nonNegotiableFilters.filter(item => item !== filter));
  };

  const handleFileUpload = async (input) => {
    const file = typeof File !== 'undefined' && input instanceof File ? input : input.target?.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt'];
    const extension = file.name?.includes('.') ? `.${file.name.split('.').pop().toLowerCase()}` : '';
    logJobDocumentUploadStep('client_file_selected', {
      file: fileMetadata(file),
      extension
    });

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
      logJobDocumentUploadStep('client_validation_failed', {
        reason: 'invalid_file_type',
        file: fileMetadata(file),
        extension,
        allowedTypes,
        allowedExtensions
      });
      setParseState({ loading: false, error: 'Upload a PDF, DOC, DOCX, or TXT file.', metadata: null, warnings: [] });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      logJobDocumentUploadStep('client_validation_failed', {
        reason: 'file_too_large',
        file: fileMetadata(file),
        maxSize: 10 * 1024 * 1024
      });
      setParseState({ loading: false, error: 'File size must be less than 10MB.', metadata: null, warnings: [] });
      return;
    }

    setUploadedFile(file);
    setPostingMethod('document_upload');
    setUploadProgress(0);
    setParseState({ loading: true, error: '', metadata: null, warnings: [] });
    logJobDocumentUploadStep('client_parse_started', {
      file: fileMetadata(file)
    });

    const result = await startImport(file, {
      notifyEmail,
      onUploadProgress: (progressEvent) => {
        const total = Number(progressEvent.total || file.size || 1);
        setUploadProgress(Math.min(100, Math.round((Number(progressEvent.loaded || 0) / total) * 100)));
      }
    });
    if (!result.success) {
      logJobDocumentUploadStep('client_parse_failed', {
        file: fileMetadata(file),
        error: result.error
      });
      setParseState({ loading: false, error: result.error, metadata: null, warnings: [] });
      return;
    }

    logJobDocumentUploadStep('client_import_accepted', {
      file: fileMetadata(file),
      jobId: result.job?.id || null,
      extractionRunId: result.processing?.id || null
    });
    setCreatedJob(result.job);
    setExtractionRunId(result.processing?.id || null);
    setAutoFollowExtraction(Boolean(result.processing?.id));
    setParseState({
      loading: false,
      error: '',
      metadata: { background: true, acceptedAt: new Date().toISOString() },
      warnings: []
    });
    setCurrentStep(2);
    toast.success('Draft created. Open each section to review extracted data as it arrives.', { duration: 6500 });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    noDragEventsBubbling: true,
    disabled: parseState.loading,
    onDropAccepted: (files) => handleFileUpload(files[0]),
    onDropRejected: () => setParseState({
      loading: false,
      error: 'Upload one valid PDF, DOC, DOCX, or TXT file smaller than 10MB.',
      metadata: null,
      warnings: []
    })
  });

  const buildJobPayload = () => ({
    postingMethod,
    title: formData.title.trim(),
    description: formData.description.trim(),
    requirementsText: formData.requirementsText.trim(),
    jobType: formData.jobType,
    experienceLevel: formData.experienceLevel,
    location: formData.location.trim(),
    isRemoteAllowed: formData.isRemoteAllowed,
    payRangeMin: formData.payRangeMin ? Number(formData.payRangeMin) : null,
    payRangeMax: formData.payRangeMax ? Number(formData.payRangeMax) : null,
    payCurrency: USD_CURRENCY,
    payPeriod: formData.payRangeMin && formData.payRangeMax ? formData.payPeriod : null,
    contractDurationMonths: formData.contractDurationMonths ? Number(formData.contractDurationMonths) : null,
    targetStartDate: formData.targetStartDate || null,
    matchBandPreference: formData.matchBandPreference,
    positionsAvailable: Number(formData.positionsAvailable) || 1,
    nonNegotiableFilters: formData.nonNegotiableFilters,
    autoPublish: false
  });

  const buildEmployerEditedPayload = () => {
    const payload = buildJobPayload();
    return Object.fromEntries(Object.entries(payload).filter(([field]) => dirtyFields[field]));
  };

  const hydrateAnswers = (setup) => {
    const nextAnswers = {};
    const nextSkipped = {};
    const questions = setup?.questions || [];
    questions.forEach((question) => {
      if (question.answer) {
        nextAnswers[question.questionKey] = jobQuestionInitialValue(question);
        nextSkipped[question.questionKey] = Boolean(question.answer.skipped);
      }
    });
    setAnswers(nextAnswers);
    setSkippedAnswers(nextSkipped);
    setDirtyProfileQuestionKeys({});
    setRecentlySavedProfileQuestionKeys({});

    const firstUnansweredIndex = questions.findIndex((question) => !question.answer || question.answer.skipped);
    const allQuestionsAnswered = questions.length > 0 && firstUnansweredIndex === -1;
    setProfileQuestionsFinished(allQuestionsAnswered);
    setActiveProfileQuestionIndex(allQuestionsAnswered ? 0 : Math.max(firstUnansweredIndex, 0));
  };

  const handleCreateJob = async () => {
    if (createdJob?.id && extractionActive) {
      const editedPayload = buildEmployerEditedPayload();
      const result = Object.keys(editedPayload).length
        ? await updateJob(createdJob.id, editedPayload)
        : { success: true, job: createdJob };
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      setCreatedJob(prev => ({ ...(prev || {}), ...(result.job || {}) }));
      toast.success('Hiring setup saved. We will notify you when the AI draft is ready.');
      navigate(`/jobs/${createdJob.id}`);
      return;
    }

    if (!validateCreateForm()) {
      toast.error('Complete the required job fields before creating the job.');
      return;
    }

    const result = createdJob?.id
      ? await updateJob(createdJob.id, buildJobPayload())
      : await createJob(buildJobPayload());
    if (!result.success) {
      if (result.error === 'EMPLOYER_PROFILE_REQUIRED') {
        setErrors({ submit: 'Create your company profile before posting jobs.' });
      } else if (result.details?.length) {
        const validationErrors = buildJobValidationErrors(result.details);
        const fields = [1, 2, 3, 4].flatMap(step => jobValidationStepFields[step] || []);
        replaceErrorsForFields([...fields, 'submit'], validationErrors);

        const firstStep = getFirstJobValidationStep(validationErrors);
        if (firstStep) setCurrentStep(firstStep);
      } else {
        setErrors({ submit: result.error });
      }
      toast.error(result.error === 'EMPLOYER_PROFILE_REQUIRED'
        ? 'Company profile is required.'
        : getFirstJobValidationMessage(buildJobValidationErrors(result.details), result.error));
      return;
    }

    setCreatedJob(prev => ({ ...(prev || {}), ...result.job }));
    if (result.response?.processing) {
      useJobProcessingStore.getState().registerRun(result.response.processing);
      setExtractionRunId(result.response.processing.id);
    }
    toast.success(createdJob?.id ? 'Draft updated.' : 'Job draft created.');

    const setupResult = await fetchJobProfileSetup(result.job.id);
    if (setupResult.success) {
      setProfileSetup(setupResult.setup);
      hydrateAnswers(setupResult.setup);
    }

    if (!formData.publishAfterCreate) {
      navigate(`/jobs/${result.job.id}`);
      return;
    }

    setAutoFollowExtraction(false);
    setCurrentStep(6);
  };

  const buildProfileAnswerPayload = () => {
    const questions = profileSetup?.questions || [];
    const payload = [];
    const answerErrors = {};

    questions.forEach((question) => {
      const rawValue = answers[question.questionKey];
      const skipped = Boolean(skippedAnswers[question.questionKey]);
      const value = toJobProfileAnswerValue(question, rawValue);

      if (question.answerType === 'object_list' && !skipped && typeof rawValue === 'string' && rawValue && value === null) {
        answerErrors[question.questionKey] = 'Use the structured fields or remove the invalid entry.';
        return;
      }

      if (!skipped && value === null) return;

      payload.push({
        questionKey: question.questionKey,
        answerValue: skipped ? null : value,
        skipped
      });
    });

    return { payload, answerErrors };
  };

  const buildProfileAnswerPayloadFor = (question, { requireAnswer = true } = {}) => {
    const answerErrors = {};
    if (!question) return { payload: [], answerErrors };

    const rawValue = answers[question.questionKey];
    const skipped = Boolean(skippedAnswers[question.questionKey]);
    const value = toJobProfileAnswerValue(question, rawValue);

    if (question.answerType === 'object_list' && !skipped && typeof rawValue === 'string' && rawValue && value === null) {
      answerErrors[question.questionKey] = 'Use the structured fields or remove the invalid entry.';
      return { payload: [], answerErrors };
    }

    if (!skipped && value === null) {
      if (requireAnswer) answerErrors[question.questionKey] = 'Answer this question or choose Skip.';
      return { payload: [], answerErrors };
    }

    return {
      payload: [{ questionKey: question.questionKey, answerValue: skipped ? null : value, skipped }],
      answerErrors
    };
  };

  const handleProfileAnswerChange = (questionKey, value) => {
    setProfileQuestionsFinished(false);
    setAnswers(prev => ({ ...prev, [questionKey]: value }));
    setDirtyProfileQuestionKeys(prev => ({ ...prev, [questionKey]: true }));
    setRecentlySavedProfileQuestionKeys(prev => {
      if (!prev[questionKey]) return prev;
      const next = { ...prev };
      delete next[questionKey];
      return next;
    });
    if (errors[questionKey]) setErrors(prev => ({ ...prev, [questionKey]: '' }));
  };

  const handleProfileSkipChange = (questionKey, checked) => {
    setProfileQuestionsFinished(false);
    setSkippedAnswers(prev => ({ ...prev, [questionKey]: checked }));
    setDirtyProfileQuestionKeys(prev => ({ ...prev, [questionKey]: true }));
    if (errors[questionKey]) setErrors(prev => ({ ...prev, [questionKey]: '' }));
  };

  const applySavedProfileQuestion = (question, result) => {
    const savedQuestion = result?.setup?.questions?.find(item => item.questionKey === question.questionKey);
    const savedAnswer = savedQuestion?.answer || result?.response?.answers?.find?.(answer => answer.questionKey === question.questionKey);

    if (result?.setup) setProfileSetup(result.setup);

    if (savedQuestion?.answer) {
      setAnswers(prev => ({ ...prev, [question.questionKey]: jobQuestionInitialValue(savedQuestion) }));
      setSkippedAnswers(prev => ({ ...prev, [question.questionKey]: Boolean(savedQuestion.answer.skipped) }));
    } else if (savedAnswer) {
      setAnswers(prev => ({ ...prev, [question.questionKey]: savedAnswer.answerValue }));
      setSkippedAnswers(prev => ({ ...prev, [question.questionKey]: Boolean(savedAnswer.skipped) }));
    }

    setErrors(prev => {
      const next = { ...prev };
      delete next[question.questionKey];
      return next;
    });
    setDirtyProfileQuestionKeys(prev => {
      const next = { ...prev };
      delete next[question.questionKey];
      return next;
    });
    setRecentlySavedProfileQuestionKeys(prev => ({ ...prev, [question.questionKey]: true }));
  };

  const saveProfileQuestion = async (question, { advance = false, finish = false } = {}) => {
    if (!createdJob?.id) {
      toast.error('Create the job draft first.');
      return false;
    }

    const { payload, answerErrors } = buildProfileAnswerPayloadFor(question);
    if (Object.keys(answerErrors).length) {
      setErrors(prev => ({ ...prev, ...answerErrors }));
      return false;
    }

    setSavingProfileQuestionKey(question.questionKey);
    const result = await saveJobProfileAnswers(createdJob.id, payload);
    setSavingProfileQuestionKey('');

    if (!result.success) {
      toast.error(result.error);
      return false;
    }

    applySavedProfileQuestion(question, result);
    if (advance) {
      const questionIndex = (profileSetup?.questions || []).findIndex(item => item.questionKey === question.questionKey);
      setActiveProfileQuestionIndex(Math.min(Math.max(questionIndex, 0) + 1, (profileSetup?.questions || []).length - 1));
    }
    if (finish) setProfileQuestionsFinished(true);
    return true;
  };

  const saveProfileSetup = async () => {
    if (!createdJob?.id || !profileSetup?.questions?.length) return { success: true };

    const { payload, answerErrors } = buildProfileAnswerPayload();
    if (Object.keys(answerErrors).length) {
      setErrors(answerErrors);
      return { success: false, error: 'Fix invalid profile answers before saving.' };
    }

    if (!payload.length) return { success: true };

    const result = await saveJobProfileAnswers(createdJob.id, payload);
    if (result.success) {
      if (result.setup) {
        setProfileSetup(result.setup);
        hydrateAnswers(result.setup);
      }
      toast.success('Matching details saved.');
    } else {
      toast.error(result.error);
    }
    return result;
  };

  const handleSaveDraftAndExit = async () => {
    const saveResult = await saveProfileSetup();
    if (saveResult.success) navigate('/jobs');
  };

  const handlePublishAndMatch = async () => {
    if (!createdJob?.id) return;
    if (createdJob.status === 'active') {
      navigate(`/jobs/${createdJob.id}/matches`);
      return;
    }
    setPublishing(true);

    const saveResult = await saveProfileSetup();
    if (!saveResult.success) {
      setPublishing(false);
      return;
    }

    let publishResult = await publishJob(createdJob.id);
    if (!publishResult.success && /already published/i.test(publishResult.error || '')) {
      publishResult = {
        success: true,
        job: { ...createdJob, status: 'active' },
        immediateMatches: [],
        matchingResults: { queued: false, alreadyPublished: true }
      };
    }

    if (!publishResult.success) {
      toast.error(publishResult.error);
      setPublishing(false);
      return;
    }

    setMatches(publishResult.immediateMatches || []);
    setMatchingResults(publishResult.matchingResults || null);
    setCreatedJob(prev => ({ ...prev, status: 'active' }));
    toast.success('Job published. Matching will finish in the background.');
    setPublishing(false);
  };

  const handleMatchAction = async (matchId, action) => {
    if (!createdJob?.id) return;
    setActionLoading(`${matchId}:${action}`);
    const result = await takeActionOnImmediateMatch(createdJob.id, matchId, action);
    if (result.success) {
      setMatches(prev => prev.map(match => (
        String(match.matchId) === String(matchId)
          ? { ...match, employerAction: action === 'not_interested' ? 'rejected' : action, actionTaken: action }
          : match
      )));
      toast.success(action === 'interested' ? 'Interest expressed.' : 'Candidate passed.');
    } else {
      toast.error(result.error);
    }
    setActionLoading(null);
  };

  const renderSourceStep = () => (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium text-sky-700">
          Drop and Go
        </span>
        <h2 className="mt-4 font-display text-2xl font-normal text-[#0b1f3a]">Give us the job description</h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          We create a secure draft immediately, then build the role and matching profile in the background. You can continue working or leave this page at any time.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-6">
        <div {...getRootProps({ 'data-job-import-dropzone': 'local' })} className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-5 py-12 text-center transition ${
          parseState.loading || isDragActive ? 'border-sky-300 bg-sky-50/60' : 'border-slate-300 bg-slate-50 hover:border-sky-300 hover:bg-sky-50/40'
        }`}>
          <span className="mb-4 flex items-center justify-center text-[#0b1f3a]">
            {parseState.loading ? <PiCircleNotchLight className="animate-spin" size={32} /> : <PiUploadSimpleLight size={32} />}
          </span>
          <span className="text-base font-medium text-[#0b1f3a]">
            {parseState.loading ? `Securing your draft... ${uploadProgress}%` : isDragActive ? 'Release to create the draft' : uploadedFile?.name || 'Drop a JD here or choose a file'}
          </span>
          <span className="mt-2 max-w-lg text-sm leading-6 text-slate-500">
            {parseState.loading
              ? 'Only the upload must finish. You will move on as soon as the durable draft exists.'
              : 'PDF, DOC, DOCX, or TXT up to 10MB. AI processing continues safely in the background.'}
          </span>
          {parseState.loading && (
            <span className="mt-5 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-sky-100">
              <span className="block h-full rounded-full bg-sky-600 transition-all" style={{ width: `${Math.max(3, uploadProgress)}%` }} />
            </span>
          )}
          <input {...getInputProps({ id: 'job-file-upload' })} />
        </div>

        <label className="mt-4 flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <input
            type="checkbox"
            checked={notifyEmail}
            onChange={(event) => setNotifyEmail(event.target.checked)}
            className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />
          <span>
            <span className="block text-sm font-medium text-[#0b1f3a]">Email me when the AI draft is ready</span>
            <span className="mt-0.5 block text-xs leading-5 text-slate-500">In-app status and completion notifications are always available.</span>
          </span>
        </label>

        {parseState.error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">
            <PiWarningCircleLight size={13} className="mt-0.5 shrink-0" />
            {parseState.error}
          </div>
        )}

        <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> Or <span className="h-px flex-1 bg-slate-200" />
        </div>

        <button
          type="button"
          onClick={() => {
            setPostingMethod('manual');
            setCurrentStep(2);
          }}
          className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-sky-200 hover:bg-sky-50/40"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-[#0b1f3a]"><PiBriefcaseLight size={14} /></span>
            <span>
              <span className="block text-sm font-medium text-[#0b1f3a]">Start from a blank job</span>
              <span className="mt-0.5 block text-xs text-slate-500">Enter the role manually and save the draft instantly.</span>
            </span>
          </span>
          <PiArrowRightLight size={13} className="text-slate-400" />
        </button>
      </div>
    </div>
  );

  const renderRoleStep = () => (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Required role information</h2>
        <p className="mt-1 text-sm text-slate-500">These fields match the backend job creation validation.</p>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className={labelClass}>Job title</label>
            {isFieldExtracting('title') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input name="title" value={formData.title} onChange={handleInputChange} className={getInputClass('title')} placeholder="Senior Frontend Engineer" />
              <FieldError message={errors.title} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Job type</label>
            {isFieldExtracting('jobType') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <select name="jobType" value={formData.jobType} onChange={handleInputChange} className={getInputClass('jobType')}>
                {jobTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
              <FieldError message={errors.jobType} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Experience level</label>
            {isFieldExtracting('experienceLevel') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <select name="experienceLevel" value={formData.experienceLevel} onChange={handleInputChange} className={getInputClass('experienceLevel')}>
                {experienceLevels.map(level => <option key={level.value} value={level.value}>{level.label}</option>)}
              </select>
              <FieldError message={errors.experienceLevel} />
            </>}
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Location</label>
            {isFieldExtracting('location') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <div className="relative">
                <PiMapPinLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input name="location" value={formData.location} onChange={handleInputChange} className={`${getInputClass('location')} pl-9`} placeholder="Nairobi, Kenya" />
              </div>
              <FieldError message={errors.location} />
              <label className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" name="isRemoteAllowed" checked={formData.isRemoteAllowed} onChange={handleInputChange} className="rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                Remote work allowed
              </label>
              <FieldError message={errors.isRemoteAllowed} />
            </>}
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Description</label>
            {isFieldExtracting('description') ? <SkeletonBlock className="h-48 w-full rounded-lg" /> : <>
              <textarea name="description" rows={8} value={formData.description} onChange={handleInputChange} className={getInputClass('description')} placeholder="Describe responsibilities, outcomes, team context, and working expectations." />
              <div className={`mt-1 flex justify-between text-xs ${errors.description ? 'text-rose-600' : 'text-slate-400'}`}>
                <span>{errors.description || 'Minimum 50 characters.'}</span>
                <span>{formData.description.length}/5000</span>
              </div>
            </>}
          </div>
        </div>
      </section>
    </div>
  );

  const renderRequirementsStep = () => (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Requirements and non-negotiables</h2>
        <p className="mt-1 text-sm text-slate-500">The backend materializes this into canonical requirements and match filters.</p>
      </div>
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
        <label className={labelClass}>Requirements text</label>
        {isFieldExtracting('requirementsText') ? <SkeletonBlock className="h-56 w-full rounded-lg" /> : <>
          <textarea name="requirementsText" rows={9} value={formData.requirementsText} onChange={handleInputChange} className={getInputClass('requirementsText')} placeholder="List required skills, years of experience, education, certifications, tools, and domain exposure." />
          <div className={`mt-1 flex justify-between text-xs ${errors.requirementsText ? 'text-rose-600' : 'text-slate-400'}`}>
            <span>{errors.requirementsText || 'Minimum 20 characters.'}</span>
            <span>{formData.requirementsText.length}/2000</span>
          </div>
        </>}

        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <label className="block text-sm font-medium text-[#0b1f3a]">Non-negotiable filters</label>
          <p className="mt-1 text-xs leading-5 text-slate-500">Add requirements that should strongly influence candidate matching.</p>
          {isFieldExtracting('nonNegotiableFilters') ? <SkeletonBlock className="mt-3 h-10 w-full rounded-lg" /> : <>
            <div className="mt-3 flex gap-2">
              <input value={newFilter} onChange={(event) => setNewFilter(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && (event.preventDefault(), addNonNegotiableFilter())} className={getInputClass('nonNegotiableFilters')} placeholder="React, payroll compliance, CPA, French" />
              <button type="button" onClick={addNonNegotiableFilter} className="inline-flex h-10 items-center rounded-lg bg-[#0b1f3a] px-3 text-white transition hover:bg-[#12345c]">
                <PiPlusLight size={13} />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {formData.nonNegotiableFilters.length ? formData.nonNegotiableFilters.map(filter => (
                <span key={filter} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                  {filter}
                  <button type="button" onClick={() => removeNonNegotiableFilter(filter)} className="text-slate-400 hover:text-rose-600">
                    <PiXLight size={12} />
                  </button>
                </span>
              )) : <span className="text-xs text-slate-400">No non-negotiable filters added.</span>}
            </div>
            <FieldError message={errors.nonNegotiableFilters} />
          </>}
        </div>
      </section>
    </div>
  );

  const renderMatchingStep = () => (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Compensation and matching behavior</h2>
        <p className="mt-1 text-sm text-slate-500">Use salary, position, timing, and band preferences to improve match precision.</p>
      </div>
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          Employer matching defaults: minimum score {matchingPreferences.minimumScoreThreshold ?? 50}%, immediate limit {matchingPreferences.immediateMatchLimit ?? 10}, preferred bands {(matchingPreferences.preferredBands || ['A', 'B']).join(', ')}.
        </div>
        {compensationConversionNotice && (
          <div className={`mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm ${
            compensationConversionNotice.tone === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-sky-200 bg-sky-50 text-sky-800'
          }`}>
            <PiWarningCircleLight className="mt-0.5 shrink-0" size={18} />
            <span>{compensationConversionNotice.message}</span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <label className={labelClass}>Minimum compensation</label>
            {isFieldExtracting('payRangeMin') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input name="payRangeMin" type="number" value={formData.payRangeMin} onChange={handleInputChange} className={getInputClass('payRangeMin')} min="1" />
              <FieldError message={errors.payRangeMin} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Maximum compensation</label>
            {isFieldExtracting('payRangeMax') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input name="payRangeMax" type="number" value={formData.payRangeMax} onChange={handleInputChange} className={getInputClass('payRangeMax')} min="1" />
              <FieldError message={errors.payRangeMax} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Currency</label>
            {isFieldExtracting('payCurrency') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input value={USD_CURRENCY} readOnly className={`${getInputClass('payCurrency')} bg-slate-50`} />
              <FieldError message={errors.payCurrency} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Pay period</label>
            {isFieldExtracting('payPeriod') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <select name="payPeriod" value={formData.payPeriod} onChange={handleInputChange} className={getInputClass('payPeriod')}>
                <option value="">Select period</option>
                {SALARY_PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
              </select>
              <FieldError message={errors.payPeriod} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Positions available</label>
            <input name="positionsAvailable" type="number" min="1" max="1000" value={formData.positionsAvailable} onChange={handleInputChange} className={getInputClass('positionsAvailable')} />
            <FieldError message={errors.positionsAvailable} />
          </div>
          <div>
            <label className={labelClass}>Contract months</label>
            {isFieldExtracting('contractDurationMonths') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input name="contractDurationMonths" type="number" min="1" max="60" value={formData.contractDurationMonths} onChange={handleInputChange} className={getInputClass('contractDurationMonths')} placeholder="Permanent if blank" />
              <FieldError message={errors.contractDurationMonths} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Target start date</label>
            {isFieldExtracting('targetStartDate') ? <SkeletonBlock className="h-[42px] w-full rounded-lg" /> : <>
              <input name="targetStartDate" type="date" value={formData.targetStartDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} className={getInputClass('targetStartDate')} />
              <FieldError message={errors.targetStartDate} />
            </>}
          </div>
          <div>
            <label className={labelClass}>Preferred match band</label>
            <select name="matchBandPreference" value={formData.matchBandPreference} onChange={handleInputChange} className={getInputClass('matchBandPreference')}>
              <option value="A">Band A - strongest fit</option>
              <option value="B">Band B - balanced fit</option>
              <option value="C">Band C - broader pool</option>
            </select>
            <FieldError message={errors.matchBandPreference} />
          </div>
          <div className="md:col-span-2">
            <label className="flex h-full min-h-[70px] items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <input type="checkbox" name="publishAfterCreate" checked={formData.publishAfterCreate} onChange={handleInputChange} className="mt-1 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
              <span>
                <span className="block text-sm font-medium text-[#0b1f3a]">Publish after matching setup</span>
                <span className="mt-1 block text-xs leading-5 text-slate-500">You will be asked for backend profile details first, then the job can publish and run matching.</span>
              </span>
            </label>
            <FieldError message={errors.publishAfterCreate} />
          </div>
        </div>
      </section>
    </div>
  );

  const renderReviewStep = () => {
    const validationErrorEntries = Object.entries(errors).filter(([field, message]) => field !== 'submit' && Boolean(message));

    const reviewFieldsMap = {
      'Title': 'title',
      'Type': 'jobType',
      'Experience': 'experienceLevel',
      'Location': 'location',
      'Positions': 'positionsAvailable',
      'Band preference': 'matchBandPreference',
      'Compensation': 'payRangeMin'
    };

    return (
      <div className="mx-auto max-w-5xl space-y-5">
        <div>
          <h2 className="font-display text-xl font-normal text-[#0b1f3a]">Review and create draft</h2>
          <p className="mt-1 text-sm text-slate-500">A draft is created first so backend profile questions can be answered against a real job ID.</p>
        </div>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              ['Title', formData.title],
              ['Type', labelize(formData.jobType)],
              ['Experience', labelize(formData.experienceLevel)],
              ['Location', `${formData.location}${formData.isRemoteAllowed ? ' · Remote allowed' : ''}`],
              ['Positions', formData.positionsAvailable],
              ['Band preference', `Band ${formData.matchBandPreference}`],
              ['Compensation', formData.payRangeMin && formData.payRangeMax ? `${USD_CURRENCY} ${Number(formData.payRangeMin).toLocaleString()} - ${Number(formData.payRangeMax).toLocaleString()} / ${(salaryPeriodLabel(formData.payPeriod) || 'Period not set').toLowerCase()}` : 'Not set'],
              ['Post-create action', formData.publishAfterCreate ? 'Continue to publish and match' : 'Save draft only']
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
                <div className="mt-1 text-sm font-medium text-[#0b1f3a]">
                  {reviewFieldsMap[label] && isFieldExtracting(reviewFieldsMap[label]) ? (
                    <SkeletonBlock className="mt-1 h-5 w-2/3 rounded-md bg-slate-200" />
                  ) : (
                    value || '...'
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Description</div>
              <div className="min-h-[96px] max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-3 text-sm leading-6 text-slate-600 whitespace-pre-wrap">
                {isFieldExtracting('description') ? (
                  <div className="space-y-2 py-1">
                    <SkeletonBlock className="h-4 w-full rounded bg-slate-200" />
                    <SkeletonBlock className="h-4 w-11/12 rounded bg-slate-200" />
                    <SkeletonBlock className="h-4 w-4/5 rounded bg-slate-200" />
                  </div>
                ) : formData.description || 'Not yet provided'}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Requirements</div>
              <div className="min-h-[96px] max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-3 text-sm leading-6 text-slate-600 whitespace-pre-wrap">
                {isFieldExtracting('requirementsText') ? (
                  <div className="space-y-2 py-1">
                    <SkeletonBlock className="h-4 w-full rounded bg-slate-200" />
                    <SkeletonBlock className="h-4 w-10/12 rounded bg-slate-200" />
                    <SkeletonBlock className="h-4 w-3/4 rounded bg-slate-200" />
                  </div>
                ) : formData.requirementsText || 'Not yet provided'}
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">
              <PiWarningCircleLight size={13} className="mt-0.5 shrink-0" />
              {errors.submit}
            </div>
          )}

          {validationErrorEntries.length > 0 && (
            <div className="mt-4 rounded-lg border border-rose-100 bg-rose-50 p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-rose-700">Fields to fix</div>
              <div className="space-y-2">
                {validationErrorEntries.map(([field, message]) => (
                  <button
                    key={field}
                    type="button"
                    onClick={() => setCurrentStep(jobValidationFieldSteps[field] || 5)}
                    className="block w-full rounded-md bg-white px-3 py-2 text-left text-xs text-rose-700 transition hover:bg-rose-100"
                  >
                    <span className="font-medium">{getJobValidationFieldLabel(field)}: </span>{message}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderEnrichmentStep = () => (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="font-display text-2xl font-normal text-[#0b1f3a]">Matching setup</h2>
          <p className="mt-1 text-sm text-slate-500">
            {jobPublished
              ? `${createdJob?.title || formData.title} is published. You can keep refining optional matching details without interrupting candidate access.`
              : `${createdJob?.title || formData.title} is saved as a draft. Add optional structured details to improve deterministic matching.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleSaveDraftAndExit} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            <PiFloppyDiskLight size={13} /> {jobPublished ? 'Save and exit' : 'Save draft'}
          </button>
          {jobPublished ? (
            <>
              <span className="inline-flex h-10 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-700">
                <PiCheckCircleLight size={15} /> Published
              </span>
              <button onClick={() => navigate(`/jobs/${createdJob.id}/matches`)} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c]">
                <PiUsersLight size={14} /> View matches
              </button>
            </>
          ) : (
            <button onClick={handlePublishAndMatch} disabled={publishing || loading} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
              {publishing ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiMagnifyingGlassLight size={13} />}
              Publish and match
            </button>
          )}
        </div>
      </div>

      <JobProfileQuestionStepper
        profileSetup={profileSetup}
        answers={answers}
        skippedAnswers={skippedAnswers}
        errors={errors}
        activeIndex={activeProfileQuestionIndex}
        finished={profileQuestionsFinished}
        savingQuestionKey={savingProfileQuestionKey}
        dirtyQuestionKeys={dirtyProfileQuestionKeys}
        recentlySavedQuestionKeys={recentlySavedProfileQuestionKeys}
        onAnswerChange={handleProfileAnswerChange}
        onSkipChange={handleProfileSkipChange}
        onActiveIndexChange={setActiveProfileQuestionIndex}
        onFinishedChange={setProfileQuestionsFinished}
        onSaveQuestion={saveProfileQuestion}
        title="Matching profile questions"
        description="Answer one structured matching question at a time. Each step saves directly to the backend job profile."
        emptyLabel={jobPublished
          ? 'No additional matching profile questions are available for this published job.'
          : 'No backend job profile questions are available. You can publish and match with the fields already provided.'}
      />

      {matchingResults && !matchingResults.queued && !matchingResults.alreadyPublished && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Candidates evaluated</div>
              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{matchingResults.candidatesEvaluated || 0}</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Matches created</div>
              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{matchingResults.matchesCreated || 0}</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Shown now</div>
              <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{matches.length}</div>
            </div>
          </div>
          {matchingResults.warning && (
            <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-700">{matchingResults.warning}</div>
          )}
        </section>
      )}

      {matches.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-[#0b1f3a]">Immediate matches</h3>
            <button onClick={() => navigate(`/jobs/${createdJob.id}/matches`)} className="text-sm font-medium text-sky-700 hover:text-sky-900">View all matches</button>
          </div>
          {matches.map(match => (
            <MatchCard
              key={match.matchId}
              match={match}
              actionLoading={Boolean(actionLoading && actionLoading.startsWith(`${match.matchId}:`))}
              onAction={handleMatchAction}
            />
          ))}
        </section>
      )}
    </div>
  );

  const renderStepContent = () => {
    if (currentStep === 1) return renderSourceStep();
    if (currentStep === 2) return renderRoleStep();
    if (currentStep === 3) return renderRequirementsStep();
    if (currentStep === 4) return renderMatchingStep();
    if (currentStep === 5) return renderReviewStep();
    return renderEnrichmentStep();
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes magic-highlight {
          0% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); background-color: #ffffff; border-color: #e2e8f0; }
          15% { box-shadow: 0 0 15px 4px rgba(56, 189, 248, 0.4); background-color: #f0f9ff; border-color: #38bdf8; }
          100% { box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); background-color: #ffffff; border-color: #e2e8f0; }
        }
        .animate-magic-highlight {
          animation: magic-highlight 1.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6 pb-16 sm:pb-20">
        <div>
          <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
            <button onClick={() => navigate('/jobs')} className="hover:text-[#0b1f3a]">Jobs</button>
            <PiCaretRightLight size={12} />
            <span className="font-medium text-[#0b1f3a]">Create job</span>
          </nav>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Job creation</p>
              <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Create Job</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Build a complete posting, add job-side matching facts, then publish and manage candidate interest.
              </p>
            </div>
            {createdJob && (
              <button onClick={() => navigate(`/jobs/${createdJob.id}`)} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
                Open job
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex min-w-[720px] items-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const active = currentStep === step.id;
              const completed = isStepComplete(step.id);
              const available = isStepAvailable(step.id);
              return (
                <div key={step.id} className="flex flex-1 items-center">
                  <button
                    type="button"
                    onClick={() => selectStep(step.id)}
                    disabled={!available}
                    aria-current={active ? 'step' : undefined}
                    title={available ? `Open ${step.label}` : step.id === 1 && createdJob ? 'The uploaded source is locked to this draft.' : `${step.label} is not available yet.`}
                    className="flex items-center gap-2 rounded-md text-left outline-none transition focus-visible:ring-2 focus-visible:ring-sky-300 disabled:cursor-not-allowed"
                  >
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg border ${active ? 'border-sky-200 bg-[#eef9ff] text-sky-700' : completed ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-400'}`}>
                      {completed ? <PiCheckCircleLight size={13} /> : <Icon size={12} />}
                    </span>
                    <span className={`text-xs font-medium uppercase tracking-[0.12em] ${active || completed ? 'text-[#0b1f3a]' : available ? 'text-slate-600' : 'text-slate-400'}`}>{step.label}</span>
                  </button>
                  {index !== steps.length - 1 && <div className={`mx-3 h-px flex-1 ${completed ? 'bg-emerald-100' : 'bg-slate-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {extractionRun && currentStep !== 1 && !extractionBackground && (
          <section className={`overflow-hidden rounded-xl border shadow-[0_18px_60px_rgba(15,23,42,0.08)] ${
            extractionRun.status === 'failed' ? 'border-rose-200 bg-rose-50' : extractionRun.status === 'completed' ? 'border-emerald-200 bg-emerald-50' : 'border-sky-200 bg-white'
          }`}>
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  extractionRun.status === 'failed' ? 'bg-rose-100 text-rose-700' : extractionRun.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#0b1f3a] text-sky-300'
                }`}>
                  {extractionRun.status === 'completed' ? <PiCheckCircleLight size={15} /> : extractionRun.status === 'failed' ? <PiWarningCircleLight size={15} /> : <PiCircleNotchLight className="animate-spin" size={15} />}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0b1f3a]">
                    {extractionRun.status === 'completed'
                      ? 'AI draft ready for your review'
                      : extractionRun.status === 'failed'
                        ? 'The draft is safe. AI processing needs attention.'
                        : 'AI is drafting the role while you complete hiring setup'}
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">
                    {extractionRun.status === 'completed'
                      ? 'Extracted sections have been merged into untouched fields. Confirm the details before publishing.'
                      : extractionRun.status === 'failed'
                        ? 'Continue manually or retry from the processing panel. No entered data was lost.'
                        : autoFollowExtraction
                          ? 'Following each extracted section automatically. Your edits remain protected as later sections arrive.'
                          : 'Automatic following is paused. Inspect any available section; your edits remain protected.'}
                  </div>
                </div>
              </div>
              <div className="min-w-[210px]">
                <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium text-slate-500">
                  <span>{labelize(extractionRun.stage)}</span>
                  <span className="flex items-center gap-2">
                    <span>{Number(extractionRun.progress || 0)}%</span>
                    {!TERMINAL_EXTRACTION_STATUSES.has(extractionRun.status) && (
                      <button
                        type="button"
                        onClick={() => setAutoFollowExtraction(value => !value)}
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition ${
                          autoFollowExtraction
                            ? 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100'
                            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                        aria-label={autoFollowExtraction ? 'Pause automatic section following' : 'Follow extraction updates'}
                        title={autoFollowExtraction ? 'Pause automatic section following' : 'Follow extraction updates'}
                      >
                        {autoFollowExtraction ? <PiPauseLight size={12} /> : <PiPlayLight size={12} />}
                      </button>
                    )}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-200/80">
                  <div className={`h-full rounded-full transition-all duration-700 ${extractionRun.status === 'failed' ? 'bg-rose-500' : extractionRun.status === 'completed' ? 'bg-emerald-500' : 'bg-sky-600'}`} style={{ width: `${Math.max(3, Number(extractionRun.progress || 0))}%` }} />
                </div>
              </div>
            </div>
          </section>
        )}

        {extractionRun && currentStep !== 1 && extractionReadyForPublish && (
          <section className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 shadow-[0_12px_36px_rgba(5,150,105,0.08)]">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                  <PiCheckCircleLight size={17} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-emerald-900">Job draft ready to review and publish</div>
                  <div className="mt-1 text-xs leading-5 text-emerald-700">
                    Core role, requirements, and compensation details are ready. Deep enrichment will continue safely in the background.
                  </div>
                </div>
              </div>
              <div className="min-w-[210px]">
                <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium text-emerald-700">
                  <span>Publish readiness</span>
                  <span>100%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-emerald-100">
                  <div className="h-full w-full rounded-full bg-emerald-500" />
                </div>
              </div>
            </div>
          </section>
        )}

        <div ref={stepContentRef} className="scroll-mt-6">
          {renderStepContent()}
        </div>
      </div>

      {currentStep !== 6 && (
        <div className="sticky bottom-0 z-30 mx-auto max-w-7xl rounded-t-lg border border-b-0 border-slate-200 bg-white/95 px-3 py-3 shadow-[0_-10px_24px_rgba(15,23,42,0.04)] backdrop-blur sm:px-4">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <button
              onClick={prevStep}
              disabled={currentStep === 1 || (Boolean(createdJob) && currentStep === 2)}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-transparent px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              <PiArrowLeftLight size={13} /> Back
            </button>

            {currentStep === 1 ? (
              <span className="text-xs text-slate-500">Upload a JD above or start from a blank job.</span>
            ) : currentStep < 5 ? (
              <button onClick={nextStep} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-5 text-sm font-medium text-white transition hover:bg-[#12345c]">
                Continue <PiArrowRightLight size={13} />
              </button>
            ) : (
              <button onClick={handleCreateJob} disabled={loading} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-5 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:opacity-60">
                {loading ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiFloppyDiskLight size={13} />}
                {extractionActive ? 'Save setup & continue later' : createdJob?.id ? 'Save reviewed draft' : 'Create draft'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateJobPage;
